/**
 *  @class
 *  @function RevealedText
 */
if (!customElements.get('revealed-text')) {
  /*
   * How each Effect setting reveals the lines as the pinned text scrolls: `mask` wraps every line in a clipping
   * mask, `enter` adds the lines' tweens. Fill and outline sweep --revealed-text-fill, which clips the solid copy
   * of each line (its data-text) that revealed-text.scss lays over it.
   */
  const effects = {
    fade: {
      enter(tl, lines) {
        lines.forEach((line) => {
          tl.fromTo(line, {
            opacity: 0.2,
            filter: "blur(5px)",
          }, {
            opacity: 1,
            filter: "blur(0px)",
            ease: "none",
            duration: 0.5,
          });
        });
      }
    },
    fill: {
      enter(tl, lines) {
        // The copy is generated text: its line's words.
        lines.forEach((line) => line.setAttribute('data-text', line.textContent.trim()));
        tl.fromTo(lines, {
          '--revealed-text-fill': 0,
        }, {
          '--revealed-text-fill': 1,
          ease: "none",
          duration: 0.5,
          stagger: 0.5,
        });
      }
    },
    rise: {
      mask: 'lines',
      enter(tl, lines) {
        tl.fromTo(lines, {
          yPercent: 105,
          // The mask's padding keeps descenders in view, so a line starts below it too.
          y: (i, line) => parseFloat(getComputedStyle(line.parentNode).paddingBottom) || 0,
          rotation: document.dir === 'rtl' ? -2 : 2,
        }, {
          yPercent: 0,
          y: 0,
          rotation: 0,
          ease: "none",
          duration: 0.5,
          stagger: 0.25,
        });
      }
    }
  };
  effects.outline = effects.fill;

  class RevealedText extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.container = this.querySelector('.revealed-text--inner');
      this.content = this.querySelector('.revealed-text--heading');
      this.effect = effects[this.dataset.effect] || effects.fade;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onMotionChange = () => this.updateMotion();
      this.reducedMotion.addEventListener('change', this.onMotionChange);
      this.updateMotion();
    }
    updateMotion() {
      this.stopMotion();
      if (this.dataset.motionEnabled !== 'true' || this.reducedMotion.matches || typeof gsap === 'undefined' || typeof SplitText === 'undefined') return;

      let _this = this;
      this.tl = gsap.timeline({
        scrollTrigger: {
          trigger: _this.container,
          start: "center center",
          pin: true,
          scrub: 1,
          invalidateOnRefresh: 1,
          anticipatePin: 1,
        }
      });
      this.setupScroll();
    }
    disconnectedCallback() {
      this.reducedMotion?.removeEventListener('change', this.onMotionChange);
      this.stopMotion();
    }
    stopMotion() {
      if (this.tl) {
        this.tl.revert();
        this.tl = null;
      }
      if (this.splittext) {
        this.splittext.revert();
        this.splittext = null;
      }
    }
    setupScroll() {
      const tl = this.tl;
      document.fonts.ready.then(() => {
        // Removed or moved (the theme editor re-renders and reorders sections) while the fonts loaded.
        if (tl !== this.tl || !this.isConnected) {
          return;
        }
        // SplitText reads line breaks left to right, so it runs the rows of a right-to-left text together. The rows
        // break at the same words either way: the text is split left to right and given its direction back at once.
        this.content.style.direction = 'ltr';
        // Split in the loaded font, so each line holds the words it shows.
        this.splittext = new SplitText(this.content, {
          type: 'lines',
          linesClass: 'revealed-text--line',
          mask: this.effect.mask,
          // The lines stay readable text: no aria-label on the block, no aria-hidden on the lines.
          aria: 'none',
        });
        this.content.style.direction = '';
        this.effect.enter(tl, this.splittext.lines);
      });
    }
  }
  customElements.define('revealed-text', RevealedText);
}
